import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Form as FormAntd } from 'antd';
import dayjs from "dayjs";

import { StepTwoFormContext } from "./StepTwoFormContext";

import { APActivitiesPerProduct, SelectOption } from "../../../../../../../../../shared/interfaces";
import { StepTwoForm } from "../../../../../utils";
import QuotationService from "../../../../../../../../../shared/services/quotation";
import { GenericDataFormContext, QuotationInfoContext } from "../../../../../shared/contexts";
import { WizardDataFormContext } from "../../../../Wizard/contexts/WizardDataForm";
import { LOADING_SAVED_QUOTATIONS_STATES, QuotationStep } from "../../../../../shared/interfaces";

const { 
    STEP_ONE_DATA_FILLED,
    STEP_TWO_DATA_FILLED,
    STEP_TWO_DATA_FILLING,
    STEP_TWO_SERVICES_LOADED,
    STEP_TWO_SERVICES_LOADING
} = LOADING_SAVED_QUOTATIONS_STATES;

export interface TableActivityItem {
    [key: string]:              any;
    key:                        string;
    activity:                   string;
    quantity:                   string;
    ageRange:                   string;
    relationship:               string;
}

export const StepTwoFormProvider: FC<PropsWithChildren> = ({ children }) => {
    const [ activities, setActivities ] = useState<Array<TableActivityItem>>([]);

    const [ productsList, setProductsList ] = useState<Array<SelectOption>>([]);
    const [ isLoadingProductsList, setIsLoadingProductsList ] = useState<boolean>(true);

    const [ apActiviesList, setApActivitiesList ] = useState<Array<APActivitiesPerProduct>>([]);
    const [ apActiviesListOptions, setApActivitiesListOptions ] = useState<Array<SelectOption>>([]);

    const [ isLoadingProductsInfo, setIsLoadingProductsInfo ] = useState(false);

    const [ stepTwoForm, setStepTwoForm ] = useState<StepTwoForm>();

    const [ propertyDataForm ] = FormAntd.useForm<StepTwoForm>();
    const [ apFormValues, setApFormValues ] = useState<StepTwoForm | undefined>();

    const planSelected: string = FormAntd.useWatch('plans', propertyDataForm);

    const [ productsSelectedHistory, setProductsSelectedHistory ] = useState<{ prevValue: string, newValue: string }>();

    const [ showOnChangeProductModal, setShowOnChangeProductModal ] = useState<boolean>(false);

    const [ planWaschanged, setPlanWaschanged ] = useState<boolean>(false);

    const { quotationInfo, currentStep, savedQuotation, loadSaveQuotationState, setLoadSaveQuotationState } = useContext( QuotationInfoContext );
    const { getAgeRangeListPerProduct, getRelationshipsListPerProduct, quotationForm } = useContext( WizardDataFormContext );
    const { setCanShowSpinner, setPlansSelected, getCoveragesPerPlanRisk } = useContext( GenericDataFormContext );

    const getProducsList = useCallback(async () => {
        setCanShowSpinner(true);
        try {
            const response = await QuotationService.getProductsListPerProducer(quotationInfo?.article!, quotationInfo?.branch!);

            if ( response.data ) {
                const productsList = response.data.productosPorProductor.map(({ codigo, descripcionLarga }) => ({ label: descripcionLarga, value: codigo }));
                setProductsList( productsList );
                setIsLoadingProductsList(false);
            }
        } catch (error) {
        } finally {
            setCanShowSpinner( false );
        }
    }, [quotationInfo, setProductsList, setIsLoadingProductsList, setCanShowSpinner])

    const getAPActiviesListPerProduct = useCallback(async ( article: string, branch: string, product: string ) => {
      try {
        const response = await QuotationService.getAPActivitiesPerProduct(article, branch, product );

        if( response.data?.actividadesApPorProducto?.length ) {
            setApActivitiesList( response.data.actividadesApPorProducto );
            const activities = response.data.actividadesApPorProducto.map(({ codigo, descripcion }) => ({ label: descripcion, value: codigo }));
            setApActivitiesListOptions( activities );
        }
    } catch (error) {
    }
    }, [setApActivitiesList, setApActivitiesListOptions])

    const loadActivitiesTableFormInfo = useCallback( async ( product: string ) => {
      if( quotationInfo?.branch && quotationInfo?.article ) {
        Promise.all([
            getAPActiviesListPerProduct( quotationInfo?.article, quotationInfo?.branch, product ),
            getAgeRangeListPerProduct( quotationInfo?.article, quotationInfo?.branch, product ),
            getRelationshipsListPerProduct( quotationInfo?.article, quotationInfo?.branch, product )
        ]).finally(() => {
            setIsLoadingProductsInfo( false );
            setCanShowSpinner( false );
        })
    }
    }, [getAPActiviesListPerProduct, getAgeRangeListPerProduct, getRelationshipsListPerProduct, quotationInfo, setCanShowSpinner])

    const showModalToConfirmSelectOtherProduct = () => setShowOnChangeProductModal( true );

    const checkProductChange = useCallback(( value: string, prevValue: string ) => {
      if( value === prevValue ) return prevValue;

      if( activities.length ) {
          setProductsSelectedHistory(() => ({ newValue: value, prevValue }));
          showModalToConfirmSelectOtherProduct();
          return prevValue;
      } 
      
      return value;
    }, [activities.length]);

    const getActivityDescriptionById  = useCallback((id: string) => apActiviesListOptions.find(el => String(el.value) === String(id))?.label, [apActiviesListOptions]);

    const fillStepTwoFromSavedQuotation = useCallback(async () => {
        setLoadSaveQuotationState(STEP_TWO_DATA_FILLING);

        const { vigenciaDesde, vigenciaHasta, planSeleccionado, actividades } = savedQuotation!;

        if(
            vigenciaDesde && 
            vigenciaHasta && 
            planSeleccionado && 
            actividades.length
        ) {
            const fromDate = dayjs(vigenciaDesde, 'DD-MM-YYYY');
            const toDate = dayjs(vigenciaHasta, 'DD-MM-YYYY');
            const quantityOfDays = toDate.diff(fromDate, 'days');

            propertyDataForm?.setFieldValue('vigencyDate', [fromDate, toDate]);
            propertyDataForm?.setFieldValue('quantityOfDays', quantityOfDays);

            propertyDataForm?.setFieldValue('plans', String(planSeleccionado));

            const activities: Array<TableActivityItem> = actividades.map((activity: any) => {
                const key = String(Math.random());

                return {
                    key,
                    activity: String(activity.codigo),
                    quantity: activity.cantidad,
                    ageRange: String(activity.rangoDeEdad),
                    relationship: String(activity.parentesco),
                }
            });
    
            setActivities(activities);

            setLoadSaveQuotationState(STEP_TWO_DATA_FILLED);
        }
    }, [
        savedQuotation, 
        propertyDataForm, 
        setActivities,
        setLoadSaveQuotationState,
    ]);

    const callServicesForSavedQuotation = useCallback(async () => {
        setLoadSaveQuotationState(STEP_TWO_SERVICES_LOADING);

        const { planSeleccionado } = savedQuotation!;

        await Promise.all([
            getProducsList(),
            loadActivitiesTableFormInfo(planSeleccionado)
        ]).finally(() => setLoadSaveQuotationState(STEP_TWO_SERVICES_LOADED));
    }, [
        setLoadSaveQuotationState, 
        savedQuotation,
        getProducsList,
        loadActivitiesTableFormInfo,
    ])

    useEffect(() => {
        if(planSelected && !loadSaveQuotationState) {
            setPlansSelected([planSelected]);
        }
    }, [planSelected, setPlansSelected, loadSaveQuotationState]);

    useEffect(() => {
        if(loadSaveQuotationState === STEP_ONE_DATA_FILLED) {
            callServicesForSavedQuotation();
        }
    }, [loadSaveQuotationState]);

    useEffect(() => {
        if(loadSaveQuotationState === STEP_TWO_SERVICES_LOADED) {
            fillStepTwoFromSavedQuotation();
        }
    }, [loadSaveQuotationState])

    useEffect(() => {
        if(quotationForm?.actividades?.length) {    
            const activitySelected = quotationForm?.actividades?.[0];
            const riskCode = String(activitySelected!.categoria);
            if (planSelected?.length) getCoveragesPerPlanRisk(riskCode, activitySelected!.codigo);
        }
      }, [planSelected, quotationForm, getCoveragesPerPlanRisk]);

    const values = useMemo(() => ({
        productsList,
        isLoadingProductsList,
        apActiviesList,
        apActiviesListOptions,
        stepTwoForm,
        setStepTwoForm,
        getAPActiviesListPerProduct,
        loadActivitiesTableFormInfo,
        isLoadingProductsInfo, 
        setIsLoadingProductsInfo,
        activities,
        setActivities,
        productsSelectedHistory,
        setProductsSelectedHistory,
        showOnChangeProductModal,
        setShowOnChangeProductModal,
        checkProductChange,
        planWaschanged, 
        setPlanWaschanged,
        propertyDataForm,
        apFormValues,
        setApFormValues,
        getProducsList,
        getActivityDescriptionById,
    }), [
        productsList,
        isLoadingProductsList,
        apActiviesList,
        apActiviesListOptions,
        stepTwoForm,
        setStepTwoForm,
        getAPActiviesListPerProduct,
        loadActivitiesTableFormInfo,
        isLoadingProductsInfo, 
        setIsLoadingProductsInfo,
        activities,
        setActivities,
        productsSelectedHistory,
        setProductsSelectedHistory,
        showOnChangeProductModal,
        setShowOnChangeProductModal,
        checkProductChange,
        planWaschanged, 
        setPlanWaschanged,
        propertyDataForm,
        apFormValues,
        setApFormValues,
        getProducsList,
        getActivityDescriptionById,
    ]);

    useEffect(() => {
        if( quotationInfo && currentStep === QuotationStep.TWO && !productsList.length ) {
            getProducsList();
        }
        /* eslint-disable-next-line */
    }, [ currentStep, quotationInfo ])

    return (
        <StepTwoFormContext.Provider value={ values }>              
            { children }
        </StepTwoFormContext.Provider>
    )
}